import { useState, useRef } from "react";
import {
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
  useTheme,
} from "@mui/material";
import {
  AddPhotoAlternateOutlined,
  DeleteOutlined,
  ZoomInOutlined,
} from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";
import { tokens } from "../../../../theme";
import type { IEquipment, IEquipmentImage } from "../../../../types/equipment";
import apiService from "../../../../service/Api";
import Button from "../../../../components/common/Button";

interface Props {
  equipment: IEquipment;
  onImagesUpdated?: () => void;
}

export default function ImagesTab({ equipment, onImagesUpdated }: Props) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);

  const images: { id?: number | string; url: string }[] = (equipment.images || []).map(
    (img: IEquipmentImage) => ({ id: img.id, url: img.image || "" }),
  );

  const handleUpload = async (file: File) => {
    setIsUploading(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append("image", file);
      formData.append("equipment", equipment.id.toString());
      await apiService.post("/api/equipments/equipment-images/", formData);
      onImagesUpdated?.();
    } catch {
      setError("Failed to upload image.");
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = async (imageId: number | string) => {
    setError(null);
    try {
      await apiService.delete(`/api/equipments/equipment-images/${imageId}/`);
      onImagesUpdated?.();
    } catch {
      setError("Failed to delete image.");
    }
  };

  return (
    <Box
      sx={{
        bgcolor: theme.palette.background.paper,
        borderRadius: "16px",
        p: 3,
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {images.length} {images.length === 1 ? "image" : "images"}
          </Typography>

          <Button
            variant="outlined"
            size="small"
            startIcon={
              isUploading ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                <AddPhotoAlternateOutlined sx={{ fontSize: 16 }} />
              )
            }
            disabled={isUploading}
            onClick={() => !isUploading && fileInputRef.current?.click()}
            sx={{
              textTransform: "none",
              borderRadius: "8px",
              fontSize: 13,
              px: 2,
              py: 0.75,
              borderColor: colors.primary[400],
              color: theme.palette.text.primary,
              "&:hover": { borderColor: theme.palette.text.primary },
            }}
          >
            {isUploading ? "Uploading..." : "Add Image"}
          </Button>

          <input
            ref={fileInputRef}
            type="file"
            hidden
            accept="image/*"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleUpload(file);
              e.target.value = "";
            }}
          />
        </Box>

        {images.length === 0 ? (
          <Typography variant="body2" color="text.secondary" textAlign="center">
            No images yet.
          </Typography>
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
              gap: 2,
            }}
          >
            <AnimatePresence>
              {images.map((img, i) => (
                <motion.div
                  key={img.url}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ delay: i * 0.04 }}
                >
                  <Box
                    sx={{
                      position: "relative",
                      borderRadius: "10px",
                      overflow: "hidden",
                      aspectRatio: "1",
                      bgcolor: colors.primary[800],
                      "&:hover .img-actions": { opacity: 1 },
                    }}
                  >
                    <img
                      src={img.url}
                      alt={`Equipment ${i + 1}`}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                    <Box
                      className="img-actions"
                      sx={{
                        position: "absolute",
                        inset: 0,
                        bgcolor: "rgba(0,0,0,0.45)",
                        opacity: 0,
                        transition: "opacity 0.2s",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 1,
                      }}
                    >
                      <IconButton
                        size="small"
                        onClick={() => setLightboxUrl(img.url)}
                        sx={{
                          bgcolor: "rgba(255,255,255,0.15)",
                          color: colors.primary[900],
                        }}
                      >
                        <ZoomInOutlined fontSize="small" />
                      </IconButton>
                      {img.id && (
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(img.id!)}
                          sx={{
                            bgcolor: "rgba(255,255,255,0.15)",
                            color: colors.primary[900],
                          }}
                        >
                          <DeleteOutlined fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  </Box>
                </motion.div>
              ))}
            </AnimatePresence>
          </Box>
        )}
                                                                              
        <AnimatePresence>
          {lightboxUrl && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setLightboxUrl(null)}
              style={{
                position: "fixed",
                inset: 0,
                background: "rgba(0,0,0,0.85)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 1400,
                cursor: "zoom-out",
              }}
            >
              <img
                src={lightboxUrl}
                alt="Preview"
                style={{ maxWidth: "90vw", maxHeight: "90vh", borderRadius: 8 }}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </Box>
    </Box>
  );
}
